import type { GraphModification, TemplateDataobject, Graph } from "@lr-notitia/notitia-designer/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import { handleGraphModification } from "./utils";
import { useDomainStore } from "../domain";
import { useGraphStore } from "../graph";
import { useSymbolsStore } from "../symbols";
import { UserRole } from "@lr-notitia/notitia-designer/schemas";

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	symbolsStore = useSymbolsStore();
}

export const templateDataobjects = (graph: Ref<Graph>) =>
	computed<TemplateDataobject[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "dataobject" && node.layer === "template"
			) as TemplateDataobject[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function templateDataobject(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => byId(id, graph),
		add: (conceptId: string, pos: { x: number; y: number }) => add(domainId, graph, conceptId, pos),
		update: (dataobject: TemplateDataobject) => update(domainId, graph, dataobject),
		remove: (id: string) => remove(domainId, graph, id),
		getAllTopLevel: (dId: string) => getAllTopLevel(graph, dId),
		getComposedByDataobjectIds: (dataobjectId: string) => getComposedByDataobjectIds(graph, dataobjectId),
		isHidden: (id: string) => isHidden(id, graph),
		toggleHide: (id: string) => toggleHide(domainId, id, graph),
	};
}

function byId(id: string, graph: Ref<Graph>): TemplateDataobject | undefined {
	let hit = graph.value.nodes[id];
	return hit && hit.type === "dataobject" && hit.layer === "template" ? (hit as TemplateDataobject) : undefined;
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	conceptId: string,
	pos: { x: number; y: number }
): Promise<GraphModification> {
	if (!graphStore || !domainStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		let concept = graphStore.concept.dataobject.byId(conceptId);
		if (!concept) return reject();
		api()
			.domain(domainId.value)
			.template()
			.dataobjects()
			.post({ concept: concept })
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, dataobject: TemplateDataobject): Promise<GraphModification> {
	if (!domainStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		if (!graph.value.nodes[dataobject.id]) return reject();
		api()
			.domain(domainId.value)
			.template()
			.dataobject(dataobject.id)
			.put(dataobject)
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	if (!domainStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.template()
			.dataobject(id)
			.del()
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}

function getAllTopLevel(graph: Ref<Graph>, dId: string): TemplateDataobject[] {
	const dataobjects = Object.values(graph.value.nodes).filter(
		(node) => node.type === "dataobject" && node.layer === "template"
	) as TemplateDataobject[];
	// return all dataobjects which do not appread in any hasA
	return dataobjects.filter((dataobject) => {
		return !dataobjects.find((d) => d.hasA.includes(dataobject.id));
	});
}
function getComposedByDataobjectIds(graph: Ref<Graph>, dataobjectId: string): string[] {
	const dataobjects = Object.values(graph.value.nodes).filter(
		(node) => node.type === "dataobject" && node.layer === "template"
	) as TemplateDataobject[];
	const composedByDataobjectIds: string[] = [];
	const getComposedByDataobjectIds = (dataobjectId: string) => {
		const dataobject = dataobjects.find((d) => d.id === dataobjectId);
		if (dataobject) {
			dataobject.composedBy.forEach((dId) => {
				composedByDataobjectIds.push(dId);
				getComposedByDataobjectIds(dId);
			});
		}
	};
	getComposedByDataobjectIds(dataobjectId);
	return composedByDataobjectIds;
}

function isHidden(id: string, graph: Ref<Graph>): boolean {
	let dataobject = byId(id, graph);
	if (!dataobject) return false;
	return dataobject.context?.["nta-hidden"] === true;
}
async function toggleHide(domainId: Ref<string>, id: string, graph: Ref<Graph>): Promise<void> {
	const dataobject = byId(id, graph);
	if (!dataobject) return;
	if (!dataobject.context) dataobject.context = {};
	dataobject.context["nta-hidden"] = !dataobject.context?.["nta-hidden"];
	update(domainId, graph, dataobject)
		.then(() => Promise.resolve())
		.catch(() => Promise.reject());
}
